import type { NewMediaUploadFn } from '../../hooks/api/useMediaUpload';
import type {
  ContentObject,
  Media,
  MediaHydrated,
  RelationWithoutHydration,
} from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_OBJECT } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import useMediaUpload from '../../hooks/api/useMediaUpload';

import { ModalInstanceContext } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import UnsplashMedia from '../../pages/MediaLibrary/Subpages/UnsplashMedia';
import UserMedia from '../../pages/MediaLibrary/Subpages/UserMedia';
import AllObjects from '../../subpages/AllObjects/AllObjects';
import CancelButton from '../Button/predefined/CancelButton/CancelButton';
import SaveButton from '../Button/predefined/SaveButton/SaveButton';
import Tabs from '../Tabs/Tabs';

const LIMIT = 10;
const MEDIA_LIMIT = 12;

type SelectedObjetData<T extends string, F extends boolean> = F extends false
  ? RelationWithoutHydration
  : T extends '_media'
    ? Media | MediaHydrated
    : ContentObject;

type SelectedObjectsProps<T extends string, F extends boolean> = Record<
  string,
  SelectedObjetData<T, F>
>;

type SelectObjectFn<T extends string> = (
  object: T extends '_media' ? Media | MediaHydrated : ContentObject,
  type: string,
) => void;

interface LinkObjectContentModalProps<T extends string, F extends boolean> {
  /**
   * Type of relation objects
   */
  relationType?: T;
  /**
   * Selected relations
   */
  selected?: SelectedObjectsProps<T, F>;
  /**
   * If multiple selection is allowed
   */
  isMultiple?: boolean;
  /**
   * On media upload callback
   */
  onMediaUpload?: NewMediaUploadFn;
  /**
   * Callback for validating selected objects before close modal
   */
  validateSelected?: (value?: SelectedObjectsProps<T, F>) => boolean;
  /**
   * Default filters for user media tab
   */
  defaultUserMediaFilters?: Record<
    string,
    { filter: string | string[]; type: string }
  >;
  /**
   * If a selected object should be select as object data
   */
  returnContentObject?: F;
  /**
   * Test id for the modal
   */
  testId?: string;
}

const LinkObjectContentModal = <
  T extends string = '',
  F extends boolean = false,
>({
  relationType,
  selected = EMPTY_OBJECT,
  isMultiple = false,
  onMediaUpload,
  returnContentObject,
  validateSelected,
  defaultUserMediaFilters,
  testId,
}: LinkObjectContentModalProps<T, F>) => {
  const { t } = useTranslation();
  const isMedia = useMemo(() => relationType === '_media', [relationType]);
  const modalInstance = use(ModalInstanceContext);
  const { newMedia, removeNewMedia } = useMediaUpload();
  const { permissions } = use(UserContext);
  const [mediaLimit, setMediaLimit] = useState(MEDIA_LIMIT);

  const canReadMedia = permissions.canCo(
    '_media',
    RolePermissions.PERMISSIONS_TYPES.READ,
  );

  const [isCleared, setIsCleared] = useState(false);
  const appearedMediaRef = useRef<{ [key: string]: boolean }>({});
  const [selectedObjects, setSelectedObjects] =
    useState<SelectedObjectsProps<T, F>>(selected);

  // Clear new media uploaded if modal was not opened
  useEffect(() => {
    removeNewMedia();
    setIsCleared(true);
  }, [removeNewMedia]);

  const selectObject = useCallback<SelectObjectFn<T>>(
    (object, type) => {
      setSelectedObjects((prevSelected) => {
        if (!object.id) return prevSelected;
        const objectData = returnContentObject
          ? object
          : {
              type: 'internal',
              dataUrl: `/api/v1/content/${type}/${object.id}`,
            };
        if (!isMultiple)
          return {
            [object.id]: objectData as SelectedObjetData<T, F>,
          };
        const newSelected = { ...prevSelected };
        if (newSelected[object.id]) delete newSelected[object.id];
        else {
          newSelected[object.id] = objectData as SelectedObjetData<T, F>;
        }
        return newSelected;
      });
    },
    [isMultiple, returnContentObject],
  );

  useEffect(() => {
    if (!isCleared || !newMedia || (relationType && !isMedia)) return;
    newMedia.forEach(({ media, state }) => {
      if (state === 'uploaded') {
        if (appearedMediaRef.current?.[media.id]) return;
        selectObject(media, '_media');
        appearedMediaRef.current[media.id] = true;
      }
    });
  }, [isCleared, isMedia, newMedia, relationType, selectObject]);

  const allObjectsContent = useCallback(
    (isTabs: boolean) => {
      if (isMedia) return null;
      return (
        <AllObjects
          limit={LIMIT}
          contentType={relationType}
          selectedObjects={selectedObjects}
          selectObject={selectObject}
          isTabs={isTabs}
          testId={testId}
        />
      );
    },
    [isMedia, selectedObjects, relationType, selectObject, testId],
  );

  const tabs = useMemo(() => {
    const tabs = [];
    if (!relationType) {
      tabs.push({
        label: t('ContentForm.AllObjects'),
        key: 'all',
        render: () => allObjectsContent(true),
      });
    }
    if (!relationType || isMedia)
      tabs.push(
        ...[
          {
            label: t('Media.Tabs.UploadedFiles'),
            key: 'userMedia',
            render: () => (
              <UserMedia
                mediaOnPageLimit={mediaLimit}
                setMediaOnPageLimit={setMediaLimit}
                selectMedia={selectObject as any}
                selectedMedia={selectedObjects}
                multipleUpload={isMultiple}
                hasPanel={false}
                onUpload={onMediaUpload as any}
                defaultFilters={defaultUserMediaFilters}
                testId={testId}
                additionalGridContainerClasses="h-full md:max-h-[calc(100vh-28rem)] px-2 md:overflow-x-clip"
                inModal
              />
            ),
            disabled: !canReadMedia,
            tooltip: !canReadMedia ? t('ContentForm.Relation.CannotRead') : '',
            tooltipPlacement: 'bottomCenter' as const,
          },
          {
            label: t('Media.Tabs.UnsplashFiles'),
            key: 'unsplashMedia',
            render: () => (
              <UnsplashMedia
                mediaOnPageLimit={mediaLimit}
                setMediaOnPageLimit={setMediaLimit}
                testId={testId}
                additionalGridContainerClasses="h-full md:max-h-[calc(100vh-28rem)] px-2"
              />
            ),
            disabled: !canReadMedia,
            tooltip: !canReadMedia ? t('ContentForm.Relation.CannotRead') : '',
            tooltipPlacement: 'bottomCenter' as const,
            phoneTooltipPlacement: 'bottomRight' as const,
          },
        ],
      );
    return tabs;
  }, [
    relationType,
    isMedia,
    t,
    canReadMedia,
    allObjectsContent,
    mediaLimit,
    selectObject,
    selectedObjects,
    isMultiple,
    onMediaUpload,
    defaultUserMediaFilters,
    testId,
  ]);

  const resolveModal = useCallback(
    (value?: SelectedObjectsProps<T, F>) => {
      const isValid = !validateSelected ? true : validateSelected(value);
      if (!isValid) return;

      modalInstance.resolve?.(value);
      removeNewMedia();
    },
    [modalInstance, removeNewMedia, validateSelected],
  );

  const onCancel = useCallback(() => {
    resolveModal();
  }, [resolveModal]);

  const onSave = useCallback(() => {
    resolveModal(selectedObjects);
  }, [resolveModal, selectedObjects]);

  return (
    <>
      {tabs.length === 0 ? (
        allObjectsContent(false)
      ) : (
        <Tabs tabs={tabs} testId={testId} />
      )}
      <div
        className="w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-5
        border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
      >
        <CancelButton
          onClick={onCancel}
          {...getTestProps(testId, 'cancel', 'testId')}
        />
        <SaveButton
          onClick={onSave}
          {...getTestProps(testId, 'save', 'testId')}
        />
      </div>
    </>
  );
};

export default LinkObjectContentModal;
